import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetContentBlock,
  ModalSheetContentTitle,
  ModalSheetFooterButtonGroup,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetContentDescription,
  ModalSheetFooterSection,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { ErrorBlock } from '@/components/ErrorBlock'
import styles from './SplitPayoutDialog.module.css'
import { formatMoney } from '@/utils/money-util'
import { dateFormatters } from '@/utils/date-util'
import type { SplitPayoutDialogSituation } from './SplitPayoutDialogViewModel'

export interface SplitPayoutDialogProps {
  showing: boolean
  situation: SplitPayoutDialogSituation
  payoutTotal: number
  allocator: ReactNode
  totalAllocatedStatus: ReactNode
  addAllocationButton: ReactNode
  splitPayoutError: unknown
  isSplitPayoutPending: boolean
  isSplitValid: boolean
  onClose: () => unknown
  onSplitPayout: () => unknown
}

export function SplitPayoutDialog({
  showing,
  situation,
  payoutTotal,
  allocator,
  totalAllocatedStatus,
  addAllocationButton,
  splitPayoutError,
  isSplitPayoutPending,
  isSplitValid,
  onClose,
  onSplitPayout,
}: SplitPayoutDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSplitPayoutPending ? undefined : onClose}
      disableCloseOnEsc
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent leftAligned>
        <ModalSheetContentBlock>
          <DialogCopy situation={situation} payoutTotal={payoutTotal} />
        </ModalSheetContentBlock>
        <ModalSheetContentBlock>{allocator}</ModalSheetContentBlock>
      </ModalSheetContent>
      {Boolean(splitPayoutError) && (
        <ErrorBlock tackOn="bottom" error={splitPayoutError} />
      )}
      {totalAllocatedStatus}
      <ModalSheetFooter>
        <ModalSheetFooterSection>{addAllocationButton}</ModalSheetFooterSection>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            onPress={onClose}
            isDisabled={isSplitPayoutPending}
          />
          <Button
            label="Split Payout"
            variant="primary"
            isDisabled={isSplitPayoutPending || !isSplitValid}
            isPending={isSplitPayoutPending}
            onPress={onSplitPayout}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

interface DialogCopyProps {
  situation: SplitPayoutDialogSituation
  payoutTotal: number
}

function DialogCopy({ situation, payoutTotal }: DialogCopyProps) {
  switch (situation.type) {
    case 'Manual': {
      return (
        <>
          <ModalSheetContentTitle>Edit split</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            A split payout of <Emphasize>{formatMoney(payoutTotal)}</Emphasize>{' '}
            as last edited by <Emphasize>{situation.allocatedByName}</Emphasize>{' '}
            on {renderTimestamp(situation.allocatedAt)}.
          </ModalSheetContentDescription>
        </>
      )
    }

    case 'Auto': {
      return (
        <>
          <ModalSheetContentTitle>Edit auto split</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            A split payout of <Emphasize>{formatMoney(payoutTotal)}</Emphasize>{' '}
            has been automatically adjusted by the system on{' '}
            {renderTimestamp(situation.adjustedAt)} and allocated to the
            selected Pros.
          </ModalSheetContentDescription>
        </>
      )
    }

    case 'Default': {
      return (
        <>
          <ModalSheetContentTitle>Split payout</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            Split payout of <Emphasize>{formatMoney(payoutTotal)}</Emphasize>{' '}
            with selected Pros.
          </ModalSheetContentDescription>
        </>
      )
    }
  }
}

function renderTimestamp(adjustedAt: Date) {
  return (
    <>
      <Emphasize>{dateFormatters.dateNumeric.format(adjustedAt)}</Emphasize> at{' '}
      <Emphasize>{dateFormatters.timeDefault.format(adjustedAt)}</Emphasize>
    </>
  )
}

interface EmphasizeProps {
  children?: ReactNode
}

function Emphasize({ children }: EmphasizeProps) {
  return <span className={styles.emphasize}>{children}</span>
}
